/**
 * Character tone at all hours (DECISIONS 2026-10-13): by day the figures sit in the world under dayGrade's flat warm
 * wash (22 % at the default level), which softens their whites and reds. As that wash gives way at dusk and night the
 * raw sprite contrast came back (inside the player's light pool at night a figure is nearly unlit). This pass carries
 * the wash on through the day cycle in the hour's own tone: warm amber at dusk and dawn, cool night blue after dark,
 * at the strength the day wash loses (`1 - dayShare`), so the total wash is continuous over the hours and the day look
 * is untouched (zero here at full day). One full-screen source-over fill after the lighting, under the day grade.
 * Follows the day grade's setting (`dayGradeLevel`: off turns this off too) and the lights-and-shadows setting.
 */
import { settings } from '../../settings';
import { lightingOn, nightAt } from '../lighting';
import { LEVELS, dayShare } from './dayGrade';
import { registerRenderPass } from './registry';

/** Dusk/dawn amber and night blue (mid tones: they pull the whites down and the darks barely up). */
const DUSK = [214, 150, 110] as const;
const NIGHT = [52, 64, 112] as const;
/** The night wash is lighter than the day/dusk one: the dark already flattens the scene. */
const NIGHT_SCALE = 0.7;

/**
 * Paints the hour's wash over a picture `W` x `H` at `hour` and level `lv` (nothing at full day). Shared with the
 * front stage (front/hourTone.ts).
 */
export function paintHourWash(
  g: CanvasRenderingContext2D,
  W: number,
  H: number,
  lv: NonNullable<(typeof LEVELS)[number]>,
  hour: number,
): void {
  const { dark, warm } = nightAt(hour);
  const share = 1 - dayShare(dark, warm);
  if (share < 0.02) return;
  const t = dark * dark * (3 - 2 * dark);
  const a = lv.soft * share * (1 - (1 - NIGHT_SCALE) * t);
  const c = (i: 0 | 1 | 2) => Math.round(DUSK[i] + (NIGHT[i] - DUSK[i]) * t);
  g.save();
  g.globalCompositeOperation = 'source-over';
  g.globalAlpha = 1;
  g.fillStyle = `rgba(${c(0)},${c(1)},${c(2)},${a.toFixed(3)})`;
  g.fillRect(0, 0, W, H);
  g.restore();
}

registerRenderPass({
  id: 'hourWash',
  layer: 'world',
  order: 405,
  draw(f) {
    const lv = LEVELS[settings.dayGradeLevel];
    if (!f.outdoors || f.scene || !lightingOn() || !lv) return;
    paintHourWash(f.g, f.W, f.H, lv, f.world.hour);
  },
});
